import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import useBlogPosts from "@/hooks/useBlogPosts";
import LexicalEditorWrapper from "@/pages/blog/components/LexicalEditor";
import PasswordGate from "@/components/base/PasswordGate";
import type { LexicalEditor } from "lexical";

export default function BlogEditPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { getPostById, createPost, updatePost } = useBlogPosts();
  const isNew = !id || id === "new";

  const existingPost = !isNew && id ? getPostById(id) : undefined;
  const editorInstanceRef = useRef<LexicalEditor | null>(null);
  const wordFileRef = useRef<HTMLInputElement>(null);
  const coverFileRef = useRef<HTMLInputElement>(null);

  const [title, setTitle] = useState("");
  const [excerpt, setExcerpt] = useState("");
  const [content, setContent] = useState("<p></p>");
  const [coverImage, setCoverImage] = useState("");
  const [category, setCategory] = useState("");
  const [tagsInput, setTagsInput] = useState("");
  const [published, setPublished] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [wordUploading, setWordUploading] = useState(false);
  const [coverUploading, setCoverUploading] = useState(false);
  const [contentLoaded, setContentLoaded] = useState(false);
  const [contentVersion, setContentVersion] = useState(0);

  useEffect(() => {
    if (existingPost) {
      setTitle(existingPost.title);
      setExcerpt(existingPost.excerpt);
      setContent(existingPost.content);
      setCoverImage(existingPost.coverImage);
      setCategory(existingPost.category);
      setTagsInput(existingPost.tags.join(", "));
      setPublished(existingPost.published);
      setContentLoaded(true);
    } else if (isNew) {
      setContentLoaded(true);
    }
  }, [existingPost, isNew]);

  const handleEditorReady = useCallback((editor: LexicalEditor) => {
    editorInstanceRef.current = editor;
  }, []);

  const handleSave = async (publish?: boolean) => {
    const willPublish = publish ?? published;
    if (!title.trim()) return;

    setSaving(true);
    setSaved(false);

    const tags = tagsInput
      .split(",")
      .map((t) => t.trim())
      .filter(Boolean);

    if (!isNew && existingPost) {
      updatePost(existingPost.id, {
        title: title.trim(),
        excerpt: excerpt.trim(),
        content,
        coverImage: coverImage.trim(),
        category: category.trim() || "Uncategorized",
        tags,
        published: willPublish,
      });
    } else {
      const newPost = createPost({
        title: title.trim(),
        excerpt: excerpt.trim(),
        content,
        coverImage: coverImage.trim(),
        category: category.trim() || "Uncategorized",
        tags,
        published: willPublish,
      });

      if (newPost) {
        setTimeout(() => {
          navigate(`/blog/admin/edit/${newPost.id}`, { replace: true });
        }, 500);
      }
    }

    setSaving(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  const handlePreview = () => {
    const tags = tagsInput
      .split(",")
      .map((t) => t.trim())
      .filter(Boolean);

    const previewHtml = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Preview: ${title || "Untitled"}</title>
  <link href="https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;0,600;1,300;1,400&family=Inter:wght@300;400;500;600&display=swap" rel="stylesheet">
  <link href="https://cdn.jsdelivr.net/npm/remixicon@4.6.0/fonts/remixicon.css" rel="stylesheet" />
  <style>
    *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: 'Inter', system-ui, sans-serif; background: #fafaf9; color: #292524; }
    .preview-bar { position: sticky; top: 0; z-index: 10; display: flex; align-items: center; justify-content: space-between; padding: 12px 24px; background: #fff; border-bottom: 1px solid #e7e5e4; }
    .preview-bar-left { display: flex; align-items: center; gap: 12px; }
    .site-name { font-family: 'Cormorant Garamond', Georgia, serif; font-size: 16px; font-weight: 500; color: #1c1917; }
    .back-btn { display: inline-flex; align-items: center; gap: 6px; font-family: 'Inter', system-ui, sans-serif; font-size: 13px; color: #57534e; background: none; border: 1px solid #d6d3d1; border-radius: 6px; padding: 6px 14px; cursor: pointer; transition: all 0.2s; text-decoration: none; }
    .back-btn:hover { background: #f5f5f4; color: #292524; border-color: #a8a29e; }
    .preview-label { font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: 1px; color: #a16207; background: #fef3c7; padding: 4px 10px; border-radius: 20px; }
    .body-wrap { padding: 40px 20px; }
    .container { max-width: 720px; margin: 0 auto; }
    .back-to-blog { display: inline-flex; align-items: center; gap: 6px; font-size: 14px; color: #78716c; text-decoration: none; margin-bottom: 32px; transition: color 0.2s; }
    .back-to-blog:hover { color: #292524; }
    .cover-img { width: 100%; max-height: 420px; object-fit: cover; border-radius: 12px; margin-bottom: 32px; }
    h1 { font-family: 'Cormorant Garamond', Georgia, serif; font-size: 2.6rem; font-weight: 400; color: #1c1917; margin-bottom: 12px; line-height: 1.2; }
    .meta { font-size: 13px; color: #78716c; margin-bottom: 32px; display: flex; align-items: center; gap: 8px; }
    .category-badge { display: inline-block; background: #fef3c7; color: #a16207; font-size: 11px; font-weight: 600; padding: 3px 10px; border-radius: 20px; text-transform: uppercase; letter-spacing: 0.5px; }
    .excerpt { font-size: 16px; color: #57534e; line-height: 1.7; margin-bottom: 32px; }
    .content h2 { font-family: 'Cormorant Garamond', Georgia, serif; font-size: 1.6rem; font-weight: 500; color: #1c1917; margin-top: 40px; margin-bottom: 16px; }
    .content h3 { font-family: 'Cormorant Garamond', Georgia, serif; font-size: 1.25rem; font-weight: 500; color: #292524; margin-top: 32px; margin-bottom: 12px; }
    .content p { font-size: 16px; line-height: 1.85; margin-bottom: 20px; color: #44403c; }
    .content ul, .content ol { margin-bottom: 20px; padding-left: 24px; }
    .content li { margin-bottom: 8px; line-height: 1.75; font-size: 16px; color: #44403c; }
    .content a { color: oklch(0.52 0.16 150); text-decoration: underline; text-underline-offset: 2px; }
    .content strong { font-weight: 600; color: #1c1917; }
    .content blockquote { border-left: 3px solid #d6d3d1; padding-left: 20px; margin: 24px 0; color: #78716c; font-style: italic; }
    .tags-section { margin-top: 40px; padding-top: 24px; border-top: 1px solid #e7e5e4; }
    .tag { display: inline-block; background: #f5f5f4; color: #78716c; font-size: 12px; padding: 4px 12px; border-radius: 20px; margin-right: 8px; margin-bottom: 8px; }
    .cta-box { margin-top: 48px; background: #f5f5f4; border-radius: 12px; padding: 32px; text-align: center; }
    .cta-box h3 { font-family: 'Cormorant Garamond', Georgia, serif; font-size: 1.4rem; font-weight: 500; color: #1c1917; margin-bottom: 8px; }
    .cta-box p { font-size: 14px; color: #78716c; margin-bottom: 16px; }
  </style>
</head>
<body>
  <div class="preview-bar">
    <div class="preview-bar-left">
      <span class="site-name">Serena's Quiet Recovery Space</span>
    </div>
    <span class="preview-label">Preview</span>
  </div>
  <div class="body-wrap">
    <div class="container">
      <a href="javascript:window.close()" class="back-to-blog">
        &larr; Back to editor
      </a>
      ${coverImage ? `<img src="${coverImage}" alt="${title}" class="cover-img" />` : ""}
      <h1>${title || "Untitled Post"}</h1>
      <div class="meta">
        ${category ? `<span class="category-badge">${category}</span>` : ""}
        <span>${new Date().toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}</span>
      </div>
      ${excerpt ? `<p class="excerpt">${excerpt}</p>` : ""}
      <div class="content">${content}</div>
      ${tags.length > 0 ? `<div class="tags-section">${tags.map((t) => `<span class="tag">${t}</span>`).join("")}</div>` : ""}
      <div class="cta-box">
        <h3>Ready to take your next step?</h3>
        <p>Personalised coaching can help you move forward with calm, clarity, and confidence.</p>
      </div>
    </div>
  </div>
</body>
</html>`;

    const blob = new Blob([previewHtml], { type: "text/html" });
    const url = URL.createObjectURL(blob);
    window.open(url, "_blank");
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  const handleWordUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setWordUploading(true);

    try {
      const mammoth = (await import("mammoth")).default;
      const arrayBuffer = await file.arrayBuffer();
      const result = await mammoth.extractRawText({ arrayBuffer });

      const plainText = result.value.trim();

      if (editorInstanceRef.current) {
        const { $getRoot, $createParagraphNode, $createTextNode } = await import("lexical");
        editorInstanceRef.current.update(() => {
          const root = $getRoot();
          root.clear();
          const paragraphs = plainText.split("\n");
          paragraphs.forEach((line) => {
            const trimmed = line.trim();
            if (trimmed) {
              const p = $createParagraphNode();
              p.append($createTextNode(trimmed));
              root.append(p);
            }
          });
          if (root.getChildrenSize() === 0) {
            root.append($createParagraphNode());
          }
        });
      }

      setContent(`<p>${plainText.replace(/\n/g, "</p><p>")}</p>`);
      setContentVersion((v) => v + 1);
    } catch {
      alert("Could not read the Word document. Please try pasting the content as plain text instead.");
    } finally {
      setWordUploading(false);
      if (wordFileRef.current) wordFileRef.current.value = "";
    }
  };

  const handleCoverUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      alert("Please select an image file.");
      if (coverFileRef.current) coverFileRef.current.value = "";
      return;
    }

    setCoverUploading(true);
    const reader = new FileReader();
    reader.onload = () => {
      setCoverImage(reader.result as string);
      setCoverUploading(false);
    };
    reader.onerror = () => {
      alert("Could not read the image file. Please try again.");
      setCoverUploading(false);
    };
    reader.readAsDataURL(file);
  };

  const renderContent = () => {
    if (!isNew && !existingPost && contentLoaded) {
      return (
        <main className="min-h-screen pt-24 md:pt-28 pb-16">
          <div className="w-full px-4 sm:px-6 lg:px-10">
            <div className="max-w-4xl mx-auto text-center py-20">
              <Link
                to="/blog/admin"
                className="inline-flex items-center gap-1.5 font-body text-sm text-foreground-600 hover:text-foreground-950 transition-colors duration-200 mb-4"
              >
                <i className="ri-arrow-left-line text-base" />
                Back to Blog Admin
              </Link>
              <p className="font-body text-foreground-500 text-sm">
                Post not found.
              </p>
            </div>
          </div>
        </main>
      );
    }

    if (!contentLoaded) {
      return (
        <main className="min-h-screen pt-24 md:pt-28 pb-16">
          <div className="flex items-center justify-center py-32">
            <div className="w-8 h-8 border-2 border-primary-300 border-t-primary-600 rounded-full animate-spin" />
          </div>
        </main>
      );
    }

    return (
      <main className="min-h-screen pt-24 md:pt-28 pb-16 md:pb-20">
        <div className="w-full px-4 sm:px-6 lg:px-10">
          <div className="max-w-4xl mx-auto">
            <header className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
              <div>
                <Link
                  to="/blog/admin"
                  className="inline-flex items-center gap-1.5 font-body text-sm text-foreground-600 hover:text-foreground-950 transition-colors duration-200 mb-2"
                >
                  <i className="ri-arrow-left-line text-base" />
                  Back to Blog Admin
                </Link>
                <h1 className="font-heading text-3xl md:text-4xl font-light text-foreground-950">
                  {isNew ? "Create new post" : "Edit post"}
                </h1>
              </div>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={handlePreview}
                  className="inline-flex items-center gap-1.5 font-body text-sm text-foreground-700 hover:text-foreground-950 px-4 py-2 rounded-md border border-background-300 hover:border-background-400 transition-all duration-200 whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-eye-line text-base" />
                  Preview
                </button>
                <button
                  type="button"
                  onClick={() => handleSave(false)}
                  disabled={saving}
                  className="inline-flex items-center gap-1.5 font-body text-sm text-foreground-700 hover:text-foreground-950 px-4 py-2 rounded-md border border-background-300 hover:border-background-400 transition-all duration-200 whitespace-nowrap cursor-pointer disabled:opacity-50"
                >
                  <i className="ri-save-line text-base" />
                  {saving ? "Saving..." : "Save Draft"}
                </button>
                <button
                  type="button"
                  onClick={() => handleSave(true)}
                  disabled={saving}
                  className="inline-flex items-center gap-1.5 bg-primary-500 text-background-50 font-body text-sm font-medium px-5 py-2.5 rounded-md hover:bg-primary-600 transition-all duration-300 whitespace-nowrap cursor-pointer disabled:opacity-50"
                >
                  <i className="ri-send-plane-line text-base" />
                  {saving ? "Publishing..." : "Publish"}
                </button>
              </div>
            </header>

            {saved && (
              <div className="mb-6 bg-primary-100 text-primary-800 font-body text-sm px-4 py-3 rounded-md flex items-center gap-2">
                <i className="ri-check-line text-base" />
                Post saved successfully
              </div>
            )}

            <div className="space-y-6">
              <div>
                <label htmlFor="post-title" className="block font-body text-sm font-medium text-foreground-800 mb-2">
                  Title
                </label>
                <input
                  id="post-title"
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Enter your blog post title..."
                  className="w-full px-4 py-3 bg-background-50 border border-background-300 rounded-md font-body text-sm text-foreground-900 placeholder-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-all duration-300"
                />
              </div>

              <div>
                <label htmlFor="post-excerpt" className="block font-body text-sm font-medium text-foreground-800 mb-2">
                  Excerpt
                </label>
                <textarea
                  id="post-excerpt"
                  value={excerpt}
                  onChange={(e) => setExcerpt(e.target.value)}
                  rows={3}
                  maxLength={300}
                  placeholder="A short summary of your post..."
                  className="w-full px-4 py-3 bg-background-50 border border-background-300 rounded-md font-body text-sm text-foreground-900 placeholder-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-all duration-300 resize-none"
                />
                <p className="mt-1 text-xs font-body text-foreground-400">
                  {excerpt.length}/300 characters
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="post-category" className="block font-body text-sm font-medium text-foreground-800 mb-2">
                    Category
                  </label>
                  <input
                    id="post-category"
                    type="text"
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                    placeholder="e.g. Emotional Wellbeing"
                    className="w-full px-4 py-3 bg-background-50 border border-background-300 rounded-md font-body text-sm text-foreground-900 placeholder-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-all duration-300"
                  />
                </div>
                <div>
                  <label htmlFor="post-tags" className="block font-body text-sm font-medium text-foreground-800 mb-2">
                    Tags
                  </label>
                  <input
                    id="post-tags"
                    type="text"
                    value={tagsInput}
                    onChange={(e) => setTagsInput(e.target.value)}
                    placeholder="e.g. mindfulness, recovery, calm"
                    className="w-full px-4 py-3 bg-background-50 border border-background-300 rounded-md font-body text-sm text-foreground-900 placeholder-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-all duration-300"
                  />
                  <p className="mt-1 text-xs font-body text-foreground-400">
                    Separate tags with commas
                  </p>
                </div>
              </div>

              <div>
                <label className="block font-body text-sm font-medium text-foreground-800 mb-2">
                  Cover Image
                </label>
                <div className="flex flex-col sm:flex-row gap-4">
                  <div className="flex-1 space-y-3">
                    <div className="flex items-center gap-3">
                      <input
                        id="post-cover"
                        type="text"
                        value={coverImage}
                        onChange={(e) => setCoverImage(e.target.value)}
                        placeholder="Paste image URL..."
                        className="flex-1 px-4 py-3 bg-background-50 border border-background-300 rounded-md font-body text-sm text-foreground-900 placeholder-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-all duration-300"
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="font-body text-xs text-foreground-400">or</span>
                      <input
                        ref={coverFileRef}
                        type="file"
                        accept="image/*"
                        onChange={handleCoverUpload}
                        className="hidden"
                        id="cover-file-upload"
                      />
                      <label
                        htmlFor="cover-file-upload"
                        className="inline-flex items-center gap-1.5 font-body text-xs text-foreground-600 hover:text-foreground-900 cursor-pointer transition-colors duration-200 whitespace-nowrap px-3 py-1.5 rounded-md border border-background-300 hover:border-background-400 bg-background-50"
                      >
                        <i className="ri-image-add-line text-sm" />
                        {coverUploading ? "Uploading..." : "Upload image"}
                      </label>
                      {coverImage && (
                        <button
                          type="button"
                          onClick={() => {
                            setCoverImage("");
                            if (coverFileRef.current) coverFileRef.current.value = "";
                          }}
                          className="inline-flex items-center gap-1 font-body text-xs text-foreground-400 hover:text-accent-600 transition-colors duration-200 whitespace-nowrap cursor-pointer"
                        >
                          <i className="ri-close-line text-sm" />
                          Remove
                        </button>
                      )}
                    </div>
                  </div>
                  {coverImage && (
                    <div className="flex-shrink-0 w-full sm:w-40 h-24 rounded-md overflow-hidden bg-background-100">
                      <img
                        src={coverImage}
                        alt="Cover preview"
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          (e.target as HTMLImageElement).style.display = "none";
                        }}
                      />
                    </div>
                  )}
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block font-body text-sm font-medium text-foreground-800">
                    Content
                  </label>
                  <div className="flex items-center gap-3">
                    <input
                      ref={wordFileRef}
                      type="file"
                      accept=".docx"
                      onChange={handleWordUpload}
                      className="hidden"
                      id="word-upload"
                    />
                    <label
                      htmlFor="word-upload"
                      className="inline-flex items-center gap-1.5 font-body text-xs text-foreground-500 hover:text-foreground-800 cursor-pointer transition-colors duration-200 whitespace-nowrap"
                    >
                      <i className="ri-file-word-2-line text-sm" />
                      {wordUploading ? "Uploading..." : "Upload Word doc (.docx)"}
                    </label>
                  </div>
                </div>
                <LexicalEditorWrapper
                  key={`${contentLoaded ? "loaded" : "loading"}-v${contentVersion}`}
                  initialHtml={content}
                  onChange={setContent}
                  onEditorReady={handleEditorReady}
                />
              </div>

              <div className="flex items-center gap-3 pt-2">
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={published}
                    onChange={(e) => setPublished(e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-9 h-5 bg-background-300 peer-checked:bg-primary-500 rounded-full peer transition-colors duration-200 after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-background-50 after:rounded-full after:h-4 after:w-4 after:transition-all after:duration-200 peer-checked:after:translate-x-4" />
                </label>
                <span className="font-body text-sm text-foreground-700">
                  {published ? "Published — visible on the blog" : "Draft — not visible on the blog"}
                </span>
              </div>
            </div>
          </div>
        </div>
      </main>
    );
  };

  return (
    <PasswordGate returnPath="/blog">
      {renderContent()}
    </PasswordGate>
  );
}